import { useState, useEffect, useRef } from 'react';

import ReactPlayer from 'react-player';

import { api, useAPIContext } from '../../../api/context';
import { useFPContext } from '../../../utils/fingerprint';
import dialogs from '../../../dialogs';
import currency from '../../../utils/currency';
import toast from '../../../utils/toast';
import Base from '../../Base';
import styles from './GroupUrlSubmit.module.scss';

const STATUS_LABEL = {
  'S': { label: 'Under Review', className: 'submitted' },
  'D': { label: 'Accepted', className: 'accepted' },
  'R': { label: 'Rejected', className: 'rejected' },
  'A': { label: 'Assigned', className: 'assigned' },
};

const ACCEPT_MAP = {
  'image': 'image/*',
  'video': 'video/*',
  'both': 'image/*,video/*',
};

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;        // 10 MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;       // 500 MB

const extractTikTokVideoId = (url) => {
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : null;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const path = url.split('?')[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi|mkv|ogv)$/.test(path);
};

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const absoluteMediaUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url}`;
};

const ActivitySubmitForm = ({ campaign, activity, handle, hashtagsRef }) => {

  const { token } = useAPIContext();

  /*
   * data
   */

  const [ dataLoading, setDataLoading ] = useState(true);
  const [ data, setData ] = useState();

  const [ hashtags, setHashtags ] = useState('');
  const [ submitPlaceholder, setSubmitPlaceholder ] = useState('');

  useEffect(() => {

    api(token).v1.campaigns.activities
      .obtain({
        campaign: campaign.id,
        activity: activity.id,
      })
      .then(res => {
        const data = res.body.data;

        setDataLoading(false);
        setData(data);

        setHashtags(
          (data.config.hashtags || [])
          .join(' ')
        );

        if (data.result?.data?.value_cleaned) {
          setSubmitPlaceholder(data.result.data.value_cleaned);
        }
      })
      .catch(err => {
        setDataLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign.id, activity.id]);

  /*
   * file (media_upload only)
   */

  const fileInputRef = useRef(null);
  const [ selectedFile, setSelectedFile ] = useState(null);
  const [ filePreview, setFilePreview ] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  /*
   * submit
   */

  const { fp } = useFPContext();

  const [ submitLoading, setSubmitLoading ] = useState(false);
  const [ submitValue, setSubmitValue ] = useState('');
  const [ submitError, setSubmitError ] = useState('');
  const [ customFieldValue, setCustomFieldValue ] = useState('');

  const isMediaUpload = activity.action === 'media_upload';

  const submit = () => {

    if (isMediaUpload) {
      if (!selectedFile) {
        setSubmitError('Please select a file.');
        return;
      }
      if (selectedFile.type.startsWith('image/') && selectedFile.size > MAX_IMAGE_SIZE) {
        setSubmitError('Image file is too large. Maximum size is 10 MB.');
        return;
      }
      if (selectedFile.type.startsWith('video/') && selectedFile.size > MAX_VIDEO_SIZE) {
        setSubmitError('Video file is too large. Maximum size is 500 MB.');
        return;
      }
    } else if (activity.action === 'url_submit' && !/\S+\.\S+/.test(submitValue)) {
      setSubmitError('Please enter a valid URL.');
      return;
    }

    if (data?.config?.custom_field_enabled && data?.config?.custom_field_required && !customFieldValue.trim()) {
      setSubmitError(`${data.config.custom_field_label} is required.`);
      return;
    }

    setSubmitError('');
    setSubmitLoading(true);

    fp.get().then(result => {

      let body;
      if (isMediaUpload) {
        body = new FormData();
        body.append('data_json', JSON.stringify({
          ...(data?.config?.custom_field_enabled && { custom_field: customFieldValue }),
        }));
        body.append('media', selectedFile);
        body.append('fingerprint', result.visitorId);
      } else {
        body = {
          data_json: JSON.stringify({
            value: submitValue,
            ...(data?.config?.custom_field_enabled && { custom_field: customFieldValue }),
          }),
          fingerprint: result.visitorId,
        };
      }

      api(token).v1.campaigns.activities.submit
        .post(
          {
            campaign: campaign.id,
            activity: activity.id,
          },
          body,
        )
        .then(res => {
          setSubmitLoading(false);

          if (res.status === 200) {
            handle(activity, res.body);
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    });
  };

  /*
   * content
   */

  const statusInfo = STATUS_LABEL[activity.user.status];
  const mediaAccept = ACCEPT_MAP[data?.config?.media_accept] || ACCEPT_MAP['both'];

  return (
    <div className={styles.activity}>
      <div className={styles.header}>
        <h3>{activity.title.external}</h3>
        <div className={styles.meta}>
          {
            activity.reward.currency &&
            activity.reward.amount && (
              <span className={styles.reward}>
                {currency.repr(activity.reward.currency)}
                {activity.reward.amount}
              </span>
            )
          }
        </div>
      </div>

      {
        statusInfo && (
          <div className={styles.statusBar}>
            <span className={`${styles.status} ${styles[statusInfo.className]}`}>
              {statusInfo.label}
            </span>
          </div>
        )
      }

      {
        activity.user.comment &&
        activity.user.status === 'R' && (
          <div className={styles.comment}>
            {activity.user.comment}
          </div>
        )
      }

      {
        dataLoading &&
        <div className={styles.loading}>
          Loading...
        </div>
      }

      {
        data && (
          <>
            {
              data.instr && (
                <div
                  className={styles.instr}
                  dangerouslySetInnerHTML={{
                    __html: data.instr
                  }}
                />
              )
            }

            {
              hashtags && (
                <div className={styles.field}>
                  <label>
                    Use these hashtags in your <i>Video Review</i> caption
                  </label>

                  <input
                    className={styles.copy}
                    type='text'
                    disabled={true}
                    aria-label='hashtags'
                    value={hashtags}
                  />

                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(hashtags)
                        .then(() => {
                          toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                        })
                        .catch(() => {
                          toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                        });
                    }}>

                    Copy Hashtags

                  </button>
                </div>
              )
            }

            {data?.config?.custom_field_enabled && (
              <div className={styles.field}>
                <label>
                  {data.config.custom_field_label}
                </label>

                <input
                  type='text'
                  aria-label='custom field'
                  disabled={submitLoading}
                  value={customFieldValue}
                  onChange={(event) => setCustomFieldValue(event.target.value)}
                />
              </div>
            )}

            {
              isMediaUpload
              ? (
                <>
                  {data.result?.media && !selectedFile && (
                    <div className={styles.previousSubmission}>
                      <label>Your submission</label>
                      <div className={styles.player}>
                        {isVideoUrl(data.result.media)
                          ? (
                            <ReactPlayer
                              src={absoluteMediaUrl(data.result.media)}
                              controls={true}
                              width='100%'
                              height='100%'
                              playsInline={true}
                            />
                          )
                          : (
                            <img
                              src={absoluteMediaUrl(data.result.media)}
                              alt='Submitted media'
                            />
                          )
                        }
                      </div>
                    </div>
                  )}

                  <div className={styles.field}>
                    <label>
                      Upload your file
                    </label>

                    <input
                      ref={fileInputRef}
                      type='file'
                      accept={mediaAccept}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />

                    {filePreview && (
                      <img
                        className={styles.preview}
                        src={filePreview}
                        alt='Preview'
                      />
                    )}

                    {selectedFile && !filePreview && (
                      <div className={styles.fileName}>
                        {selectedFile.name}
                      </div>
                    )}

                    <button
                      type='button'
                      disabled={submitLoading}
                      onClick={() => fileInputRef.current?.click()}>
                      {selectedFile ? 'Change File' : 'Choose File'}
                    </button>

                    <button
                      type='button'
                      disabled={submitLoading || !selectedFile}
                      onClick={() => submit()}>

                      {submitLoading
                        ? <span className={styles.spinner} aria-label='submitting' />
                        : (activity.user.status === 'S' || activity.user.status === 'R'
                            ? 'Re-submit' : 'Submit')
                      }

                    </button>

                    {
                      submitError && (
                        <div className={styles.error}>
                          {submitError}
                        </div>
                      )
                    }

                    {
                      hashtagsRef && (
                        <span
                          className={styles.hashtagsLink}
                          onClick={() => hashtagsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                          Use Hashtags Above ☝️
                        </span>
                      )
                    }
                  </div>
                </>
              )
              : (
                <div className={styles.field}>
                  <label>
                    Paste your <i>Video Review</i> link
                    <span>Use Share &rarr; Copy link</span>
                  </label>

                  <input
                    type='url'
                    aria-label='link'
                    disabled={submitLoading}
                    placeholder={submitPlaceholder}
                    value={submitValue}
                    onChange={(event) => setSubmitValue(event.target.value)}
                  />

                  <button
                    type='button'
                    disabled={submitLoading}
                    onClick={() => submit()}>

                    {activity.user.status === 'S' || activity.user.status === 'R'
                      ? 'Re-submit' : 'Submit'}

                  </button>

                  {
                    submitError && (
                      <div className={styles.error}>
                        {submitError}
                      </div>
                    )
                  }

                  {
                    hashtagsRef && (
                      <span
                        className={styles.hashtagsLink}
                        onClick={() => hashtagsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                        Use Hashtags Above ☝️
                      </span>
                    )
                  }
                </div>
              )
            }

            {data.config.sample_videos?.length > 0 && (
              <div className={styles.videos}>
                <h3>✨ Check out these videos for inspiration! 🎬</h3>
                {data.config.sample_videos.map((url, index) => {
                  const videoId = extractTikTokVideoId(url);
                  if (!videoId) return null;
                  return (
                    <div key={videoId}>
                      <iframe
                        title={`Sample video ${index + 1}`}
                        src={`https://www.tiktok.com/embed/v2/${videoId}`}
                        loading={index > 0 ? 'lazy' : undefined}
                        allowFullScreen
                        allow='encrypted-media'
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )
      }
    </div>
  );
};

const GroupUrlSubmit = ({ campaign, group, handle }) => {

  const { setDialog } = dialogs.utils.useDialog();

  const _handle = (activity, data) => {
    handle(activity, data);
    setDialog();
  };

  const hashtagsRef = useRef(null);
  const groupHashtags = (group.config?.hashtags || []).join(' ');

  return (
    <Base title='Submit'>
      <div className={styles.root}>
        {
          group.instr && (
            <div
              className={styles.groupInstr}
              dangerouslySetInnerHTML={{
                __html: group.instr
              }}
            />
          )
        }

        {
          groupHashtags && (
            <div ref={hashtagsRef} className={styles.field}>
              <label>
                Use these hashtags in your <i>Video Review</i> caption
              </label>

              <input
                className={styles.copy}
                type='text'
                disabled={true}
                aria-label='hashtags'
                value={groupHashtags}
              />

              <button
                type='button'
                onClick={() => {
                  navigator.clipboard.writeText(groupHashtags)
                    .then(() => {
                      toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                    })
                    .catch(() => {
                      toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                    });
                }}>

                Copy Hashtags

              </button>
            </div>
          )
        }

        <div className={styles.activities}>
          {
            group.activities &&
            group.activities.map(activity => (
              <ActivitySubmitForm
                key={activity.id}
                campaign={campaign}
                activity={activity}
                handle={_handle}
                hashtagsRef={groupHashtags ? hashtagsRef : null}
              />
            ))
          }
        </div>

        {group.config?.sample_videos?.length > 0 && (
          <div className={styles.videos}>
            <h3>✨ Check out these videos for inspiration! 🎬</h3>
            {group.config.sample_videos.map((url, index) => {
              const videoId = extractTikTokVideoId(url);
              if (!videoId) return null;
              return (
                <div key={videoId}>
                  <iframe
                    title={`Sample video ${index + 1}`}
                    src={`https://www.tiktok.com/embed/v2/${videoId}`}
                    loading={index > 0 ? 'lazy' : undefined}
                    allowFullScreen
                    allow='encrypted-media'
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Base>
  );
};

export default GroupUrlSubmit;
